import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import Disclaimer from '../components/Disclaimer';
import { getMarketReturn, INDEXES, YEARS } from '../data/marketReturns';

const fmtPct = n => `${n > 0 ? '+' : ''}${n}%`;
const fmtPctRaw = n => `${n}%`;

const INDEX_LABELS = {
  sp500: 'S&P 500',
  nasdaq: 'NASDAQ',
  'dow-jones': 'Dow Jones',
  bitcoin: 'Bitcoin',
};

const INDEX_COLORS = {
  sp500: '#38bdf8',
  nasdaq: '#a78bfa',
  'dow-jones': '#22c55e',
  bitcoin: '#f59e0b',
};

export default function MarketReturns() {
  const { index, year } = useParams();
  const data = getMarketReturn(index, year);

  if (!data) {
    return (
      <div style={s.page}>
        <div style={s.inner}>
          <h1 style={s.h1}>Index or year not found</h1>
          <p style={s.body}>We have market returns for S&P 500, NASDAQ, Dow Jones, and Bitcoin from 2015–2025.</p>
          <Link to="/market-returns/sp500/2024" style={s.ctaBtn}>View S&P 500 2024 returns →</Link>
        </div>
      </div>
    );
  }

  const { yearData, displayName, emoji, description, disclaimer, tenYearAvg } = data;
  const yearNum = parseInt(year, 10);
  const isPositive = yearData.return >= 0;
  const accentColor = INDEX_COLORS[index] || '#38bdf8';
  const isBitcoin = index === 'bitcoin';

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `What was the ${displayName} return in ${yearNum}?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `The ${displayName} returned ${fmtPct(yearData.return)} in ${yearNum}. ${yearData.context}`,
        },
      },
      {
        '@type': 'Question',
        name: `What is the average annual ${displayName} return?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `Based on data from 2015–2025, the ${displayName} averaged approximately ${fmtPctRaw(tenYearAvg)} per year. The long-term historical average for the S&P 500 is approximately 10% annually. Past performance does not guarantee future results.`,
        },
      },
      {
        '@type': 'Question',
        name: `Should I invest in the ${displayName}?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `Index funds that track the ${displayName} are a popular, low-cost way to invest in broad market performance. Consult a financial advisor to determine if index investing aligns with your goals and risk tolerance. Past performance does not guarantee future results.`,
        },
      },
    ],
  };

  // All years for this index
  const allYears = YEARS;
  const otherIndexes = INDEXES.filter(i => i !== index);

  return (
    <div style={s.page}>
      <SEO
        title={`${displayName} Annual Return ${yearNum} | KnowFi`}
        description={`The ${displayName} returned ${fmtPct(yearData.return)} in ${yearNum}. ${yearData.context.slice(0, 120)}...`}
        path={`/market-returns/${index}/${yearNum}`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div style={{ ...s.glow, background: `radial-gradient(ellipse at center, ${accentColor}0d 0%, transparent 65%)` }} />
      <div style={s.inner}>

        {/* Breadcrumb */}
        <div style={s.breadcrumb}>
          <Link to="/" style={s.breadLink}>Home</Link>
          <span style={s.breadSep}>›</span>
          <Link to="/blog?tab=data" style={s.breadLink}>Data & Tools</Link>
          <span style={s.breadSep}>›</span>
          <span style={s.breadLink}>{displayName}</span>
          <span style={s.breadSep}>›</span>
          <span style={s.breadCurrent}>{yearNum}</span>
        </div>

        {/* Header */}
        <div style={s.header}>
          <div style={{ ...s.indexBadge, background: `${accentColor}18`, borderColor: `${accentColor}40`, color: accentColor }}>
            {emoji} {displayName} · {yearNum}
          </div>
          <h1 style={s.h1}>{displayName} Annual Return: {yearNum}</h1>
          <p style={s.subtitle}>{description}</p>
        </div>

        {/* Hero stats */}
        <div style={s.heroGrid}>
          <div style={s.heroCard}>
            <div style={s.heroLabel}>{yearNum} Annual Return</div>
            <div style={{ ...s.heroStat, color: isPositive ? '#22c55e' : '#ef4444', fontSize: 'clamp(32px, 6vw, 52px)' }}>
              {fmtPct(yearData.return)}
            </div>
            <div style={s.heroSub}>{isPositive ? '📈 Up year' : '📉 Down year'}</div>
          </div>
          <div style={s.heroCard}>
            <div style={s.heroLabel}>2015–2025 Avg Return</div>
            <div style={{ ...s.heroStat, color: accentColor }}>{fmtPct(parseFloat(tenYearAvg))}</div>
            <div style={s.heroSub}>11-year average</div>
          </div>
          <div style={s.heroCard}>
            <div style={s.heroLabel}>Year Context</div>
            <div style={{ fontSize: '13px', color: '#94a3b8', lineHeight: 1.55 }}>{yearData.context.slice(0, 100)}...</div>
          </div>
        </div>

        {/* Context */}
        <div style={s.contextBox}>
          <div style={s.contextLabel}>📰 What happened in {yearNum}?</div>
          <p style={s.body}>{yearData.context}</p>
          {yearData.keyEvents && (
            <ul style={s.eventList}>
              {yearData.keyEvents.map((evt, i) => (
                <li key={i} style={s.eventItem}>{evt}</li>
              ))}
            </ul>
          )}
        </div>

        {/* Bitcoin disclaimer */}
        {isBitcoin && disclaimer && (
          <div style={s.btcWarning}>
            <div style={s.btcWarningLabel}>⚠️ High Risk Asset</div>
            <p style={s.btcWarningText}>{disclaimer}</p>
          </div>
        )}

        {/* Annual returns table */}
        <div style={s.sectionTitle}>{displayName} — All Years (2015–2025)</div>
        <div style={s.table}>
          {allYears.map((y, i) => {
            const yd = data.years[y];
            if (!yd) return null;
            const isCurrentYear = y === yearNum;
            const pos = yd.return >= 0;
            return (
              <div key={y} style={{ ...s.tableRow, ...(i === allYears.length - 1 ? { borderBottom: 'none' } : {}), ...(isCurrentYear ? { background: `${accentColor}0a` } : {}) }}>
                <div style={s.tableKey}>
                  <Link to={`/market-returns/${index}/${y}`} style={{ ...s.tableLink, ...(isCurrentYear ? { color: accentColor, fontWeight: '700' } : {}) }}>
                    {y}{isCurrentYear ? ' ← Current' : ''}
                  </Link>
                </div>
                <div style={{ ...s.tableVal, color: pos ? '#22c55e' : '#ef4444' }}>
                  {fmtPct(yd.return)}
                </div>
              </div>
            );
          })}
        </div>

        {/* CTAs */}
        <div style={s.ctaSection}>
          <div style={s.ctaTitle}>📊 Track and understand the markets</div>
          <div style={s.ctaRow}>
            <Link to="/market" style={s.ctaBtn}>📈 Market Overview</Link>
            <Link to="/ask" style={s.ctaBtnSecondary}>🤖 Ask KnowFi AI</Link>
          </div>
        </div>

        {/* FAQ */}
        <div style={s.sectionTitle}>Frequently Asked Questions</div>
        <div style={s.faqList}>
          {faqSchema.mainEntity.map((item, i) => (
            <div key={i} style={s.faqItem}>
              <div style={s.faqQ}>{item.name}</div>
              <div style={s.faqA}>{item.acceptedAnswer.text}</div>
            </div>
          ))}
        </div>

        {/* Other indexes */}
        <div style={s.sectionTitle}>Compare Other Indexes for {yearNum}</div>
        <div style={s.relatedGrid}>
          {otherIndexes.map(idx => (
            <Link key={idx} to={`/market-returns/${idx}/${yearNum}`} style={s.relatedCard}>
              <div style={s.relatedLabel}>{INDEX_LABELS[idx]}</div>
              <div style={{ fontSize: '12px', color: INDEX_COLORS[idx] || '#38bdf8' }}>{yearNum} returns →</div>
            </Link>
          ))}
        </div>

        <div style={s.exploreRow}>
          <div style={s.exploreLabel}>Explore More Data</div>
          <div style={s.exploreLinks}>
            <Link to="/savings-by-age/30" style={s.exploreLink}>📈 Savings by Age</Link>
            <Link to="/savings-by-state/texas" style={s.exploreLink}>🗺️ Savings by State</Link>
            <Link to="/contribution-limits/roth-ira/2024" style={s.exploreLink}>💰 Contribution Limits</Link>
          </div>
        </div>

        <Disclaimer />
      </div>
    </div>
  );
}

const s = {
  page:  { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: '40px 24px 80px', position: 'relative', overflow: 'hidden' },
  glow:  { position: 'absolute', top: '-60px', left: '50%', transform: 'translateX(-50%)', width: '700px', height: '400px', pointerEvents: 'none' },
  inner: { maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 1 },

  breadcrumb:   { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '32px', flexWrap: 'wrap' },
  breadLink:    { color: '#475569', textDecoration: 'none', fontSize: '13px' },
  breadSep:     { color: '#334155', fontSize: '13px' },
  breadCurrent: { color: '#64748b', fontSize: '13px' },

  header:     { marginBottom: '32px' },
  indexBadge: { display: 'inline-block', fontSize: '12px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px', border: '1px solid', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px' },
  h1:         { fontSize: 'clamp(22px, 4vw, 36px)', fontWeight: '900', margin: '0 0 12px', letterSpacing: '-0.5px', lineHeight: 1.2, color: '#f1f5f9' },
  subtitle:   { fontSize: '16px', color: '#64748b', margin: 0, lineHeight: 1.65 },

  heroGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '24px' },
  heroCard: { background: '#1e293b', borderRadius: '12px', padding: '20px', border: '1px solid #334155', textAlign: 'center' },
  heroLabel:{ fontSize: '11px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', fontWeight: '600' },
  heroStat: { fontSize: 'clamp(28px, 5vw, 44px)', fontWeight: '900', letterSpacing: '-1px', lineHeight: 1.1, marginBottom: '6px' },
  heroSub:  { fontSize: '12px', color: '#475569' },

  contextBox:  { background: '#1e293b', borderRadius: '12px', padding: '20px 22px', marginBottom: '24px', border: '1px solid #334155' },
  contextLabel:{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '10px' },
  body:        { fontSize: '15px', color: '#94a3b8', lineHeight: 1.75, margin: 0 },
  eventList:   { margin: '12px 0 0', paddingLeft: '20px' },
  eventItem:   { fontSize: '14px', color: '#64748b', lineHeight: 1.6, marginBottom: '4px' },

  btcWarning:      { background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '12px', padding: '16px 20px', marginBottom: '24px' },
  btcWarningLabel: { fontSize: '12px', fontWeight: '700', color: '#ef4444', textTransform: 'uppercase', marginBottom: '8px' },
  btcWarningText:  { fontSize: '13px', color: '#94a3b8', margin: 0, lineHeight: 1.65 },

  sectionTitle: { fontSize: '13px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px', marginTop: '28px' },

  table:    { background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', overflow: 'hidden', marginBottom: '8px' },
  tableRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 18px', borderBottom: '1px solid #334155' },
  tableKey: { fontSize: '13px', color: '#64748b', flex: 1 },
  tableVal: { fontSize: '14px', fontWeight: '700', textAlign: 'right' },
  tableLink:{ color: '#64748b', textDecoration: 'none' },

  faqList: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '8px' },
  faqItem: { background: '#1e293b', borderRadius: '12px', padding: '18px 20px', border: '1px solid #334155' },
  faqQ:    { fontSize: '15px', fontWeight: '700', color: '#f1f5f9', marginBottom: '8px' },
  faqA:    { fontSize: '14px', color: '#94a3b8', lineHeight: 1.75, margin: 0 },

  exploreRow:      { marginBottom: '28px', marginTop: '28px' },
  exploreLabel:    { fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' },
  exploreLinks:    { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  exploreLink:     { background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', fontSize: '13px', fontWeight: '600', padding: '8px 14px', borderRadius: '8px', textDecoration: 'none' },
  ctaSection:      { background: 'rgba(56,189,248,0.04)', border: '1px solid rgba(56,189,248,0.15)', borderRadius: '14px', padding: '24px', marginTop: '32px', marginBottom: '28px', textAlign: 'center' },
  ctaTitle:        { fontSize: '17px', fontWeight: '700', color: '#f1f5f9', marginBottom: '16px' },
  ctaRow:          { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' },
  ctaBtn:          { background: '#38bdf8', color: '#0f172a', padding: '12px 24px', borderRadius: '8px', textDecoration: 'none', fontWeight: '800', fontSize: '14px' },
  ctaBtnSecondary: { background: '#1e293b', color: '#f1f5f9', padding: '12px 24px', borderRadius: '8px', textDecoration: 'none', fontWeight: '700', fontSize: '14px', border: '1px solid #334155' },

  relatedGrid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px', marginBottom: '28px' },
  relatedCard:  { background: '#1e293b', borderRadius: '10px', padding: '14px', border: '1px solid #334155', textDecoration: 'none', color: '#f8fafc' },
  relatedLabel: { fontSize: '14px', fontWeight: '700', color: '#f1f5f9', marginBottom: '4px' },
};
